import { SocketContext } from '@gr/portal/contexts/SocketContext';
import { TagAttributes } from '@gr/shared/entities';
import { SocketEventNameEnum } from '@gr/shared/enums';
import {
  IContactTag,
  IConversationDetail,
  IFreeFormResponse,
  IHttpResponse,
  IOptoutConversationRequest,
  ITemplateResponse,
  ITwoWayConversationEntity
} from '@gr/shared/models';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { axiosGet, axiosPost, axiosPut } from '../../authAxios';
import { NotificationType, useNotifications } from '../../contexts/NotificationContext';
import useTwoWayAvailableCampaigns from '../../hooks/useTwoWayAvailableCampaigns';
import { Dropdown } from '@Wonder-Cave/ui';
import { Button } from '../shared/Buttons/Button';
import { NewButton } from '../shared/Buttons/NewButton';
import { ButtonVariantEnum } from '../shared/Buttons/types';
import MarkCompletedPanel from '../shared/DetailsPanel/MarkCompletedPanel';
import { IDropdownValue } from '../shared/Form/Dropdown';
import SearchField from '../shared/Form/SearchField';
import { ActiveMessage } from './ActiveMessage';
import { Messages } from './Messages';
import { Responses } from './Responses';
import TagsModal from './TagsModal';
import { TagOption } from './types';

export const Conversations = () => {
  const [searchText, setSearchText] = useState('');
  const [completeLoading, setCompleteLoading] = useState(false);
  const [activeMessageLoading, setActiveMessageLoading] = useState(false);
  const [optOutLoading, setOptOutLoading] = useState(false);
  const [replyLoading, setReplyLoading] = useState(false);
  const [activeMessage, setActiveMessage] = useState<ITwoWayConversationEntity>();
  const [activeResponse, setActiveResponse] = useState<ITemplateResponse | IFreeFormResponse>();
  const [lastConvo, setLastConvo] = useState<IConversationDetail>();
  const [sentConvos, setSentConvos] = useState<Partial<IConversationDetail>[]>([]);
  const [activeConvoClientId, setActiveConvoClientId] = useState<string>();
  const [clientTags, setClientTags] = useState<TagAttributes[]>([]);
  const [currentTags, setCurrentTags] = useState<TagOption[]>([]);
  const [showTags, setShowTags] = useState(false);
  const [loadingCurrentTags, setLoadingCurrentTags] = useState(false);
  const [savingTags, setSavingTags] = useState(false);
  const [filterClientId, setFilterClientId] = useState<string | undefined>();
  const [filterCampaignId, setFilterCampaignId] = useState<string | undefined>();
  const { addNotification } = useNotifications();
  const socketManager = useContext(SocketContext);
  const [showModal, setShowModal] = useState(false);
  const queryClient = useQueryClient();
  const { query: campaignsQuery } = useTwoWayAvailableCampaigns();

  const {
    data: conversationsData,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['conversations', { clientId: filterClientId, campaignId: filterCampaignId }],
    queryFn: async ({ pageParam }) => {
      if (pageParam === 0) {
        return axiosPost('/two-way-convos-get', { clientId: filterClientId ?? null, campaignId: filterCampaignId ?? null });
      }
      return axiosPost('/two-way-convos-assign', { limit: 20, clientId: filterClientId ?? null, campaignId: filterCampaignId ?? null });
    },
    initialPageParam: 0,
    getNextPageParam: (_lastPage, allPages, lastPageParam) => {
      const total = (allPages.flatMap((p) => p?.data?.data ?? []) as ITwoWayConversationEntity[]).length;
      if (lastPageParam === 0 && total === 0 && !filterClientId && !filterCampaignId) {
        return undefined;
      }
      if (total >= 20) {
        return undefined;
      }
      return allPages.length;
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const bootstrapMutation = useMutation({
    mutationFn: async () => {
      return axiosPost('/two-way-convos-assign', { limit: 20, clientId: null, campaignId: null });
    },
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ['conversations'] });
    },
  });

  const assignedConvos: ITwoWayConversationEntity[] = conversationsData?.pages.flatMap((p) => p?.data?.data ?? []) ?? [];
  const loading = isFetching || isFetchingNextPage || bootstrapMutation.isPending;

  const clientDropdownOptions: IDropdownValue[] = [
    { label: 'All Clients', value: null, alwaysVisible: true },
    ...Array.from(
      new Map(
        (campaignsQuery.data ?? []).map((cam) => [cam.clientId, cam.clientName])
      ).entries()
    )
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([id, name]) => ({ label: name, value: id })),
  ];

  const campaignDropdownOptions: IDropdownValue[] = [
    { label: 'All Campaigns', value: null, alwaysVisible: true },
    ...(campaignsQuery.data ?? [])
      .filter((cam) => !filterClientId || cam.clientId === filterClientId)
      .map((cam) => ({ label: cam.name, value: cam.id })),
  ];

  const selectedClientOption = clientDropdownOptions.find((o) => o.value === (filterClientId ?? null));

  const selectedCampaignOption = campaignDropdownOptions.find((o) => o.value === (filterCampaignId ?? null));

  const pages = conversationsData?.pages ?? [];
  const noMoreConversations =
    pages.length > 1 &&
    ((pages[pages.length - 1]?.data?.data ?? []) as ITwoWayConversationEntity[]).length === 0;

  useEffect(() => {
    const unsubscribeUnassignConversations = socketManager!.subscribe(
      SocketEventNameEnum.CONVERSATIONS_UNASSIGNED,
      () => {
        setActiveMessage(undefined);
        queryClient.resetQueries({ queryKey: ['conversations'] });
      }
    );

    return () => {
      unsubscribeUnassignConversations();
    };
  }, []);

  useEffect(() => {
    getTags();
  }, [activeMessage]);

  const getTags = async () => {
    try {
      setLoadingCurrentTags(true);
      await Promise.all([
        getClientTags(),
        getActiveConvoTags()
      ]);
    } finally {
      setLoadingCurrentTags(false);
    }
  };

  const getClientTags = async () => {
    if (!activeMessage || activeMessage.clientId === activeConvoClientId) {
      return;
    }

    try {
      const clientId = activeMessage.clientId;
      const resp = await axiosGet<TagAttributes[]>(`/tags?clientId=${clientId}`);
      if (resp.data) {
        setActiveConvoClientId(clientId);
        setClientTags(resp.data);
      }
    } catch (e) {
      console.log('Error loading tags:', e);
      setClientTags([]);
      addNotification({
        header: 'Error',
        content: 'Failed to load client tags',
        type: NotificationType.FAILURE,
      });
    }
  };

  const getActiveConvoTags = async () => {
    if (!activeMessage) {
      return;
    }

    try {
      const resp = await axiosGet<IHttpResponse<IContactTag[]>>(`/two-way-convos/${activeMessage.id}/tags`);
      if (resp.data?.data) {
        setCurrentTags(resp.data.data.map(t => ({
          id: t.tagId,
          name: t.name,
          checked: true
        })));
      }
    } catch (e) {
      console.log('Error loading convo tags:', e);
      setClientTags([]);
      addNotification({
        header: 'Error',
        content: 'Failed to load conversation tags',
        type: NotificationType.FAILURE,
      });
    }
  };

  const createOptOut = async () => {
    setOptOutLoading(true);
    try {
      const request: IOptoutConversationRequest = {
        twoWayConversationId: activeMessage?.id ?? '',
        conversationId: lastConvo?.id ?? '',
      };
      await axiosPost('/two-way-convos-optout', request);
      setActiveMessage(undefined);
      setActiveResponse(undefined);
      queryClient.resetQueries({ queryKey: ['conversations'] });
    } catch (e) {
      console.log(e);
    } finally {
      setOptOutLoading(false);
    }
  };

  const markCompleted = async () => {
    setCompleteLoading(true);
    try {
      await axiosPost<IHttpResponse<void>>('/two-way-convos-complete', { conversationId: Array.of(activeMessage?.id) });
      setActiveMessage(undefined);
      queryClient.resetQueries({ queryKey: ['conversations'] });
    } catch (e) {
      console.log(e);
    } finally {
      setCompleteLoading(false);
    }
  };

  const saveTags = async (selectedTags: TagOption[]) => {
    const currentTagsSnapshot = currentTags.slice();

    try {
      const currentTagNames = currentTags.map(t => t.name);
      const selectedTagNames = selectedTags.map(t => t.name);
      if (currentTagNames.length === selectedTagNames.length && currentTagNames.every(name => selectedTagNames.includes(name))) {
        return;
      }

      setSavingTags(true);

      await axiosPut(`/two-way-convos/tags`, {
        twoWayConversationId: activeMessage!.id,
        clientId: activeMessage!.clientId,
        phoneNumber: activeMessage!.clientNumber,
        tagIds: selectedTags.map(t => t.id)
      });

      setCurrentTags(selectedTags);

    } catch (e) {
      console.log('Error saving tags:', e);
      setCurrentTags(currentTagsSnapshot);
      addNotification({
        header: 'Error',
        content: 'Failed to save conversation tags',
        type: NotificationType.FAILURE
      });
    } finally {
      setSavingTags(false);
      setShowTags(false);
    }
  };

  const markAllConversionRead = async () => {
    try {
      setCompleteLoading(true);
      const assignedConvosIds = assignedConvos.map((convos) => convos.id);
      await axiosPost<IHttpResponse<void>>('/two-way-convos-complete', { conversationId: assignedConvosIds });
      setActiveMessage(undefined);
      queryClient.resetQueries({ queryKey: ['conversations'] });
    } catch (e) {
      console.log(e);
    } finally {
      setCompleteLoading(false);
    }
  };

  const handleClientFilterChange = (selected?: IDropdownValue) => {
    const nextClientId = selected?.value ?? undefined;
    // Evict cached data for the filter combo we're switching to so the next
    // fetch returns fresh assignments. removeQueries skips an immediate refetch
    // of the outgoing key (vs resetQueries) — the new query subscribes after
    // the state updates below.
    queryClient.removeQueries({
      queryKey: ['conversations', { clientId: nextClientId, campaignId: undefined }],
      exact: true,
    });
    setFilterClientId(nextClientId);
    setFilterCampaignId(undefined);
    setActiveMessage(undefined);
  };

  const handleCampaignFilterChange = (selected?: IDropdownValue) => {
    const nextCampaignId = selected?.value ?? undefined;
    queryClient.removeQueries({
      queryKey: ['conversations', { clientId: filterClientId, campaignId: nextCampaignId }],
      exact: true,
    });
    setFilterCampaignId(nextCampaignId);
    setActiveMessage(undefined);
  };

  return (
    <>
      <div className="flex gap-x-4 w-full items-center">
        <div className="flex-1 grow">
          <h1>Conversations</h1>
        </div>
        <div className="flex flex-1 gap-4 [&_input]:font-bold [&_input]:text-black">
          {campaignsQuery.isPending ? (
            <>
              <div className="w-full bg-gray-200 rounded-lg h-7 animate-pulse" />
              <div className="w-full bg-gray-200 rounded-lg h-7 animate-pulse" />
            </>
          ) : (
            <>
              <Dropdown
                label=""
                value={selectedClientOption}
                onChange={handleClientFilterChange}
                options={clientDropdownOptions}
                optionLabelClassName="truncate"
                full
              />
              <Dropdown
                label=""
                value={selectedCampaignOption}
                onChange={handleCampaignFilterChange}
                options={campaignDropdownOptions}
                optionLabelClassName="truncate"
                disabled={!campaignsQuery.data}
                full
              />
            </>
          )}
        </div>
        <div className="flex flex-1 flex-col items-end">
          <NewButton
            disabled={!hasNextPage || loading}
            onClick={() => fetchNextPage()}
            isLoading={loading}
          >
            LOAD MORE CONVERSATIONS
          </NewButton>
          <div className="mt-2 body-text-small">You can only be assigned 20 conversations at a time</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4" style={{ height: '90%' }}>
        <div className="flex flex-col flex-1 max-h-full space-y-6 grow">
          <SearchField
            id="ph-search"
            name="ph-search"
            placeholder="Search by phone number"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <div className="ps-5 font-semibold text-black underline gap-10">
            <Link
              onClick={(e) => {
                e.preventDefault();
                setShowModal(true);
              }}
              to=""
              target="_blank"
            >
              Mark All As Complete
            </Link>
          </div>
          <Messages
            searchText={searchText}
            activeMessage={activeMessage}
            messages={assignedConvos}
            setActiveMessage={setActiveMessage}
            setActiveResponse={setActiveResponse}
            loading={loading}
            setActiveMessageLoading={setActiveMessageLoading}
            replyLoading={optOutLoading || completeLoading || replyLoading}
            setSentConvos={setSentConvos}
            hasNextPage={hasNextPage}
            hasActiveFilter={!!filterClientId || !!filterCampaignId}
            noMoreConversations={noMoreConversations}
            onClearFilters={() => {
              queryClient.removeQueries({
                queryKey: ['conversations', { clientId: undefined, campaignId: undefined }],
                exact: true,
              });
              setFilterClientId(undefined);
              setFilterCampaignId(undefined);
              setActiveMessage(undefined);
            }}
            onBootstrap={() => bootstrapMutation.mutate()}
            bootstrapLoading={bootstrapMutation.isPending}
          />
        </div>
        <div className="flex flex-1 max-h-full">
          <ActiveMessage
            message={activeMessage}
            activeResponse={activeResponse}
            setActiveResponse={setActiveResponse}
            getConversations={() => queryClient.resetQueries({ queryKey: ['conversations'] })}
            setLastConvo={setLastConvo}
            setActiveMessage={setActiveMessage}
            disableSend={optOutLoading || completeLoading}
            loading={activeMessageLoading}
            optoutLoading={optOutLoading}
            setLoading={setActiveMessageLoading}
            replyLoading={replyLoading}
            setReplyLoading={setReplyLoading}
            sentConvos={sentConvos}
            setSentConvos={setSentConvos}
          />
        </div>
        <div className="flex flex-col items-center flex-1 max-h-full overflow-hidden">
          <Responses
            activeMessage={activeMessage}
            activeResponse={activeResponse}
            setActiveResponse={setActiveResponse}
          />
          {activeMessage && clientTags?.length ?
            <Button
              className="px-0 my-4 mt-1 underline disabled:cursor-auto hover:cursor-pointer"
              variant={ButtonVariantEnum.TEXT_PRIMARY}
              isLoading={activeMessageLoading || loadingCurrentTags}
              text={currentTags?.length ? `Conversation Tags (${currentTags.length})` : '+ Add Conversation Tags'}
              onClick={() => {
                setShowTags(true);
              }}
            />
            : ''
          }
          <div className="flex mt-auto">
            <NewButton
              className="mr-4"
              variant={ButtonVariantEnum.TERTIARY}
              disabled={
                !activeMessage?.id ||
                optOutLoading ||
                completeLoading ||
                activeMessageLoading ||
                replyLoading ||
                loading
              }
              onClick={markCompleted}
              isLoading={completeLoading}
            >
              MARK AS COMPLETE
            </NewButton>
            <NewButton
              disabled={
                !activeMessage?.id ||
                optOutLoading ||
                completeLoading ||
                activeMessageLoading ||
                replyLoading ||
                loading
              }
              variant={ButtonVariantEnum.SECONDARY}
              onClick={() => createOptOut()}
              isLoading={optOutLoading}
            >
              OPT OUT
            </NewButton>
          </div>
        </div>
        <MarkCompletedPanel
          showModal={showModal} isCampaignActive={false}
          onClose={() => {
            setShowModal(false);
          }}
          onSuccess={() => {
            markAllConversionRead();
            setShowModal(false);
          }}
          onCancel={() => {
            setShowModal(false);
          }}>
        </MarkCompletedPanel>
      </div>

      {activeMessage && !activeMessageLoading && clientTags?.length && !loadingCurrentTags &&
        <TagsModal
          show={showTags}
          loading={savingTags}
          convo={activeMessage}
          options={clientTags}
          currentSelectedTags={currentTags}
          onSave={saveTags}
          onClose={() => setShowTags(false)}
        />
      }
    </>
  );
};
